/**
 * components/hazard/InspectionLevelSection.js — Үзлэгийн шат сонгох хэсэг.
 *
 * Шатны товчнууд (1-р, 2-р, 3-р, 4-р).
 *
 * Props:
 *   value     : 1 | 2 | 3 | 4
 *   onChange  : (level: number) => void
 *   levels    : Optional — өөр шатнууд (default 1-4)
 */
import React from "react";
import { Card, SectionTitle } from "./common";

/**
 * Үзлэгийн 4 шат — өнгө / нэршил / хэн хийх (лавлах сан, жагсаалтын badge
 * зэрэгт дахин ашиглана). Шат бүрийн эзэн нь hazardModes.js дэх горимын
 * тохиргоотой нийцнэ (bn_violation → 1-2, hab_dept_violation → 3-4).
 */
import InspectionStartButton from "src/components/inspection/InspectionStartButton";
// Төрөл → хуудасны шийдвэр НЭГ цэгээс (гар утасны хостой ижил, тесттэй):
//   cd hse_mobile && node scripts/test-inspection-flow.js
import {
  DIRECTIVE_STAGE_LABEL,
  STAGE_OF_LEVEL,
  inspectionFlowFor,
} from "./inspectionFlow";

export const INSPECTION_LEVELS = [
  {
    level: 1,
    label: "1-р шат",
    color: "#16A34A",
    icon: "fa-user-check",
    desc: "Ажилтан / ээлжийн ахлагчийн шатны үзлэг",
  },
  {
    level: 2,
    label: "2-р шат",
    color: "#0EA5E9",
    icon: "fa-users",
    desc: "Хэсэг, цехийн шатны үзлэг (БН ХАБ ажилтан)",
  },
  {
    level: 3,
    label: "3-р шат",
    color: "#EA580C",
    icon: "fa-user-tie",
    desc: "БН / цехийн удирдлагын шатны үзлэг (ХАБ хэлтсийн мэргэжилтэн)",
  },
  {
    level: 4,
    label: "4-р шат",
    color: "#A32D2D",
    icon: "fa-industry",
    desc: "Үйлдвэрийн удирдлагын шатны үзлэг",
  },
];

export const getInspectionLevel = (lv) =>
  INSPECTION_LEVELS.find((x) => String(x.level) === String(lv)) || null;

const LEVEL_COLOR = Object.fromEntries(
  INSPECTION_LEVELS.map((x) => [x.level, x.color]),
);

const LEVEL_LABEL = Object.fromEntries(
  INSPECTION_LEVELS.map((x) => [x.level, x.label]),
);

// Үзлэгийн шат → ХЭМАБМТ-06-ийн шатны код.
// IV шатанд ХАБ хэлтэс болон холбогдох хэлтсийн мэргэжилтэн тус бүр өөрийн
// шалгах хуудастай (20 ширхэг) тул хуудсаа сонгож бөглөнө. Хэн ямар хуудас
// хариуцахыг гүйцэтгэгчийн лавлахаас (xab.inspection_actor) тогтооно.
export default function InspectionLevelSection({
  value,
  onChange,
  levels = [1, 2, 3, 4],
  disabledLevels = [],
  // Зөрчлийн маягтад шат сонгосны дараа тухайн шатны ХЯНАЛТЫН ХУУДСЫГ
  // бөглөх боломж санал болгоно (журмын дагуу шалгалт хийж зөрчил илрүүлдэг).
  showChecklist = false,
  // Сонгогдсон ҮЗЛЭГИЙН ТӨРӨЛ — удирдамжаар явагдах эсэхийг шийднэ
  inspectionType,
  // Маягтад сонгогдсон нэгж — хяналтын хуудсанд өвлүүлнэ
  bnCode,
  bnName,
  tsehCode,
  tsehName,
}) {
  /* Бүх шийдвэр НЭГ цэгээс — ./inspectionFlow.js.
     `inspectionType` огт дамжуулаагүй (төрлийн сонголтгүй горим) бол хуучин
     зан төлөв хэвээр: 1-4 товч гарна. */
  const typeAware = inspectionType !== undefined;
  const flow = inspectionFlowFor(inspectionType, value);
  const showLevels = typeAware ? flow.showLevels : true;
  const directiveStage = flow.directiveStage;
  const stageCode = typeAware ? flow.fixedStage : STAGE_OF_LEVEL[value];

  if (typeAware && !flow.visible) return null;

  return (
    <Card>
      <SectionTitle>
        {showLevels ? "Үзлэгийн шат" : "Шалгах хуудас"}
      </SectionTitle>
      <div style={{ display: showLevels ? "flex" : "none", gap: 8 }}>
        {levels.map((lv) => {
          const color = LEVEL_COLOR[lv] || "#64748B";
          const active = value === lv;
          const disabled = disabledLevels.includes(lv);
          return (
            <button
              key={lv}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && onChange?.(lv)}
              title={disabled ? "Энэ шатны эрх танд байхгүй" : undefined}
              style={{
                flex: 1,
                padding: "12px 4px",
                borderRadius: 10,
                border: "2px solid " + (active ? color : "#E6E9EF"),
                background: disabled ? "#F1F2F4" : active ? color : "#fff",
                color: disabled ? "#A8AFBC" : active ? "#fff" : color,
                fontSize: 13,
                fontWeight: 700,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.55 : 1,
                transition: "all 0.15s",
                boxShadow: active ? `0 2px 8px ${color}55` : "none",
              }}
            >
              {LEVEL_LABEL[lv] || `${lv}-р шат`}
            </button>
          );
        })}
      </div>

      {/* Удирдамжаар явагдах төрөл сонгогдсон үед дугаарын шат нь утгагүй
          болохыг ТОДОРХОЙ хэлнэ — эс бөгөөс хэрэглэгч 1-4-ийн аль нэгийг
          сонгосон хэвээр байгаад «яагаад зорилтот хуудас гарахгүй байна» гэж
          эргэлзэнэ. */}
      {directiveStage && (
        <div
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            background: "#F5F0FF",
            border: "1px solid #D3C4F5",
            fontSize: 12,
            color: "#531DAB",
            lineHeight: 1.5,
          }}
        >
          <i className="fas fa-info-circle" style={{ marginRight: 6 }} />
          <b>{DIRECTIVE_STAGE_LABEL[directiveStage]}</b> нь журмаар тогтсон
          маягтгүй — шалгах асуудал нь <b>удирдамжаас</b> бүрдэнэ. Доорх товчийг
          дарж лавлахад бүртгэсэн удирдамжаа сонгоно.
        </div>
      )}

      {/* Хяналтын хуудсыг бөглөх боломж. Гурван тохиолдол:
            level_by_level  → сонгосон ШАТНЫ (I-IV) батлагдсан хуудас
            удирдамжийнх   → тухайн төрөлд тохирох УДИРДАМЖ
            бусад (Төлөвлөгөөт) → шат тогтоохгүй, цонхон дотор өөрөө сонгоно */}
      {showChecklist && (showLevels ? !!stageCode : true) && (
        <div style={{ marginTop: 10 }}>
          <InspectionStartButton
            // showLevels үед л дугаарын шат тогтооно. «Төлөвлөгөөт» гэх мэт
            // шаттай шууд буулгагдахгүй төрөлд ФИКС ХИЙХГҮЙ — хэрэглэгч
            // цонхон дотроос эрхийнхээ хүрээнд сонгоно. Өмнө нь энд үргэлж
            // STAGE_OF_LEVEL[value] («I») очдог тул «Төлөвлөгөөт» сонгосон ч
            // 1-р шатны хуудас нээгддэг байв.
            fixedStage={stageCode || undefined}
            bnCode={bnCode}
            bnName={bnName}
            tsehCode={tsehCode}
            tsehName={tsehName}
            buttonLabel={
              directiveStage
                ? `${DIRECTIVE_STAGE_LABEL[directiveStage]} — удирдамжаар шалгах`
                : showLevels
                  ? `${LEVEL_LABEL[value] || value} — хяналтын хуудас бөглөх`
                  : "Хяналтын хуудас бөглөх"
            }
            buttonStyle={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 10,
              border: `1px dashed ${
                directiveStage
                  ? "#531DAB"
                  : showLevels
                    ? LEVEL_COLOR[value] || "#0F6E56"
                    : "#0F6E56"
              }`,
              background: "#fff",
              color: directiveStage
                ? "#531DAB"
                : showLevels
                  ? LEVEL_COLOR[value] || "#0F6E56"
                  : "#0F6E56",
              fontSize: 12.5,
              fontWeight: 700,
              cursor: "pointer",
            }}
          />
        </div>
      )}
    </Card>
  );
}
